import React, { useState, useMemo } from "react";
import MathText from "../VueQuestion/MathText";
import SeparateurRubrique from "../general/SeparateurRubrique";
import { partitionnerParRubrique } from "../../utils/rubriqueAttendus";

const AddSavoirModal = ({ show, onClose, onSave, themes, selectedAttendus, selectedSavoirs, handleSavoirChange, handleAttenduChange }) => {
  const [selectedTheme, setSelectedTheme] = useState("");
  const [activeTab, setActiveTab] = useState("attendus");
  const [expandedDescripteur, setExpandedDescripteur] = useState(null);
  // Sections Objectifs/Automatismes repliables (cf. panel des attendus) :
  // choix explicite prime ; défaut fermé sauf si la section contient un coché.
  const [choixSections, setChoixSections] = useState({});
  const toggleSection = (cle, ouvertActuel) =>
    setChoixSections(prev => ({ ...prev, [cle]: !ouvertActuel }));
  const [searchQuery, setSearchQuery] = useState("");

  // Theme courant
  const currentTheme = useMemo(() => {
    if (!themes || !selectedTheme) return null;
    return themes.find((t) => t.id === parseInt(selectedTheme, 10));
  }, [themes, selectedTheme]);

  // Descripteurs du theme
  const descripteurs = useMemo(() => {
    if (!currentTheme) return [];
    return currentTheme.descripteur || [];
  }, [currentTheme]);

  // Verifier si des savoirs_faires existent
  const hasSavoirsFaires = useMemo(() => {
    return descripteurs.some(desc => desc.savoirs_faires && desc.savoirs_faires.length > 0);
  }, [descripteurs]);

  // Resultats de recherche
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;

    const query = searchQuery.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const results = { savoirs: [], attendus: [] };

    descripteurs.forEach(desc => {
      (desc.savoirs_faires || []).forEach(s => {
        if ((s.nom || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query)) {
          results.savoirs.push({ ...s, descripteurNom: desc.nom });
        }
      });
      (desc.attendus || []).forEach(a => {
        if ((a.description || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query)) {
          results.attendus.push({ ...a, descripteurNom: desc.nom });
        }
      });
    });

    return results;
  }, [descripteurs, searchQuery]);

  const renderTextWithLineBreaks = (text) => {
    if (!text) return '';
    return text.split('<br>').map((line, index, array) => (
      <React.Fragment key={index}>
        <MathText>{line}</MathText>
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-lg md:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-500 to-violet-400" />
          <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 100" preserveAspectRatio="none">
            <path fill="white">
              <animate attributeName="d" dur="4s" repeatCount="indefinite"
                values="M0,50 Q100,20 200,50 Q300,80 400,50 L400,100 L0,100 Z;
                        M0,50 Q100,80 200,50 Q300,20 400,50 L400,100 L0,100 Z;
                        M0,50 Q100,20 200,50 Q300,80 400,50 L400,100 L0,100 Z" />
            </path>
          </svg>

          <div className="relative px-3 py-3 sm:px-6 sm:py-5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="p-2 sm:p-3 bg-white/20 backdrop-blur rounded-lg sm:rounded-xl border border-white/30 flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-xl font-bold text-white truncate">
                  Ajouter des elements
                </h2>
                <p className="text-white/80 text-xs sm:text-sm">
                  Attendus et savoirs-faires
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">

          {/* Selection du theme */}
          <div className="mb-3 sm:mb-4">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Theme *
            </label>
            <select
              value={selectedTheme}
              onChange={(e) => {
                setSelectedTheme(e.target.value);
                setExpandedDescripteur(null);
                setSearchQuery("");
              }}
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-200 rounded-lg sm:rounded-xl text-xs sm:text-sm text-gray-700
                focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none transition-all duration-200 bg-white"
            >
              <option value="">-- Selectionner un theme --</option>
              {themes.map((theme) => (
                <option key={theme.id} value={theme.id}>
                  {theme.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Tabs + Recherche (visible si theme selectionne) */}
          {selectedTheme && currentTheme && (
            <div className="border-t pt-3 sm:pt-4">
              {/* Tabs */}
              <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                {hasSavoirsFaires && (
                  <button
                    onClick={() => setActiveTab("savoirs")}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg text-[10px] sm:text-xs font-medium transition-all ${
                      activeTab === "savoirs"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    Savoirs-faires
                  </button>
                )}
                <button
                  onClick={() => setActiveTab("attendus")}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg text-[10px] sm:text-xs font-medium transition-all ${
                    activeTab === "attendus"
                      ? "bg-emerald-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Attendus
                </button>
              </div>

              {/* Recherche */}
              <div className="mb-2 sm:mb-3">
                <div className="relative">
                  <svg className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 sm:pl-9 text-gray-700 pr-8 sm:pr-4 py-1.5 sm:py-2 border border-gray-200 rounded-md sm:rounded-lg text-xs sm:text-sm
                      focus:border-indigo-400 focus:outline-none transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2"
                    >
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Liste */}
              <div className="space-y-1.5 sm:space-y-2">
                {searchResults ? (
                  <>
                    {activeTab === "savoirs" && (
                      searchResults.savoirs.length > 0 ? (
                        searchResults.savoirs.map((savoir) => (
                          <div key={savoir.id}>
                            <span className="inline-block mb-1 ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 bg-blue-100 text-blue-600 text-[9px] sm:text-[10px] rounded-full">
                              {savoir.descripteurNom}
                            </span>
                            <ItemOption
                              label={renderTextWithLineBreaks(savoir.nom)}
                              onChange={() => handleSavoirChange(savoir)}
                              checked={selectedSavoirs?.some((s) => s.id === savoir.id)}
                              type="savoir"
                            />
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-gray-400 text-xs sm:text-sm py-3 sm:py-4">Aucun resultat</p>
                      )
                    )}
                    {activeTab === "attendus" && (
                      searchResults.attendus.length > 0 ? (
                        searchResults.attendus.map((attendu) => (
                          <div key={attendu.id}>
                            <span className="inline-block mb-1 ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 bg-emerald-100 text-emerald-600 text-[9px] sm:text-[10px] rounded-full">
                              {attendu.descripteurNom}
                            </span>
                            <ItemOption
                              label={renderTextWithLineBreaks(attendu.description)}
                              onChange={() => handleAttenduChange(attendu)}
                              checked={selectedAttendus?.some((a) => a.id === attendu.id)}
                              type="attendu"
                            />
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-gray-400 text-xs sm:text-sm py-3 sm:py-4">Aucun resultat</p>
                      )
                    )}
                  </>
                ) : (
                  descripteurs.map((desc) => (
                    <div key={desc.id} className="border border-gray-200 rounded-md sm:rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedDescripteur(expandedDescripteur === desc.id ? null : desc.id)}
                        className={`w-full flex items-center justify-between p-2 sm:p-3 text-left transition-all ${
                          expandedDescripteur === desc.id ? "bg-gray-100" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                          <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">{desc.nom}</span>
                        </div>
                        <svg className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 transition-transform flex-shrink-0 ${
                          expandedDescripteur === desc.id ? "rotate-180" : ""
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {expandedDescripteur === desc.id && (
                        <div className="p-1.5 sm:p-2 border-t border-gray-100 space-y-1">
                          {activeTab === "savoirs" ? (
                            desc.savoirs_faires?.length > 0 ? (
                              desc.savoirs_faires.map((savoir) => (
                                <ItemOption
                                  key={savoir.id}
                                  label={renderTextWithLineBreaks(savoir.nom)}
                                  onChange={() => handleSavoirChange(savoir)}
                                  checked={selectedSavoirs?.some((s) => s.id === savoir.id)}
                                  type="savoir"
                                />
                              ))
                            ) : (
                              <p className="text-[10px] sm:text-xs text-gray-400 text-center py-1.5 sm:py-2">Aucun savoir-faire</p>
                            )
                          ) : (
                            desc.attendus?.length > 0 ? (
                              (() => {
                                const renderAtt = (attendu) => (
                                  <ItemOption
                                    key={attendu.id}
                                    label={renderTextWithLineBreaks(attendu.description)}
                                    onChange={() => handleAttenduChange(attendu)}
                                    checked={selectedAttendus?.some((a) => a.id === attendu.id)}
                                    type="attendu"
                                  />
                                );
                                const { objectifs, automatismes } = partitionnerParRubrique(desc.attendus);
                                if (automatismes.length === 0) return desc.attendus.map(renderAtt);
                                const cleObjs = `${desc.id}::objs`;
                                const cleAutos = `${desc.id}::autos`;
                                const contientCoche = (liste) => liste.some(a => selectedAttendus?.some(sa => sa.id === a.id));
                                const objsOuverts = choixSections[cleObjs] !== undefined ? choixSections[cleObjs] : contientCoche(objectifs);
                                const autosOuverts = choixSections[cleAutos] !== undefined ? choixSections[cleAutos] : contientCoche(automatismes);
                                return (
                                  <>
                                    {objectifs.length > 0 ? (
                                      <SeparateurRubrique rubrique="objectif" count={objectifs.length}
                                        ouvert={objsOuverts} onToggle={() => toggleSection(cleObjs, objsOuverts)} />
                                    ) : null}
                                    {objsOuverts ? objectifs.map(renderAtt) : null}
                                    <SeparateurRubrique rubrique="automatisme" count={automatismes.length}
                                      ouvert={autosOuverts} onToggle={() => toggleSection(cleAutos, autosOuverts)} />
                                    {autosOuverts ? automatismes.map(renderAtt) : null}
                                  </>
                                );
                              })()
                            ) : (
                              <p className="text-[10px] sm:text-xs text-gray-400 text-center py-1.5 sm:py-2">Aucun attendu</p>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 flex-shrink-0">
          {/* Preview des selections */}
          {(selectedSavoirs.length > 0 || selectedAttendus.length > 0) && (
            <div className="p-2 sm:p-3 border-b border-gray-200">
              <div className="p-2 sm:p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg sm:rounded-xl">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-[10px] sm:text-xs font-semibold text-emerald-700">
                    {selectedSavoirs.length + selectedAttendus.length} element(s) selectionne(s)
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 sm:gap-1.5 max-h-20 sm:max-h-24 overflow-y-auto">
                  {selectedSavoirs.map((s) => (
                    <span
                      key={s.id}
                      className="inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-700 text-[9px] sm:text-[10px] rounded-full"
                    >
                      <span className="max-w-[80px] sm:max-w-[120px] truncate">{s.nom}</span>
                      <button
                        onClick={() => handleSavoirChange(s)}
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full bg-blue-200 hover:bg-blue-300 flex items-center justify-center transition-colors"
                      >
                        <svg className="w-2 h-2 sm:w-2.5 sm:h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {selectedAttendus.map((a) => (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-emerald-100 text-emerald-700 text-[9px] sm:text-[10px] rounded-full"
                    >
                      <span className="max-w-[80px] sm:max-w-[120px] truncate">{a.description}</span>
                      <button
                        onClick={() => handleAttenduChange(a)}
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full bg-emerald-200 hover:bg-emerald-300 flex items-center justify-center transition-colors"
                      >
                        <svg className="w-2 h-2 sm:w-2.5 sm:h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Bouton fermer */}
          <div className="p-3 sm:p-4">
            <button
              onClick={onClose}
              className="w-full px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg sm:rounded-xl font-medium transition-all shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 text-xs sm:text-sm"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour une option selectionnable
const ItemOption = ({ label, onChange, checked, type }) => (
  <label className={`
    flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg sm:rounded-xl cursor-pointer transition-all duration-200
    ${checked
      ? type === "savoir"
        ? "bg-blue-50 border-2 border-blue-300"
        : "bg-emerald-50 border-2 border-emerald-300"
      : "bg-gray-50 border-2 border-transparent hover:bg-gray-100"
    }
  `}>
    <div className={`
      mt-0.5 w-4 h-4 sm:w-5 sm:h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0
      ${checked
        ? type === "savoir"
          ? "bg-blue-500 border-blue-500"
          : "bg-emerald-500 border-emerald-500"
        : "border-gray-300"
      }
    `}>
      {checked && (
        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
    <span className="text-xs sm:text-sm text-gray-700 leading-relaxed flex-1 min-w-0">
      {label}
    </span>
    <input type="checkbox" checked={checked} onChange={onChange} className="hidden" />
  </label>
);

export default AddSavoirModal;
